import React, { memo } from 'react';
import { useAppSelector } from 'hooks';
import { BillTypes, Cart, CurrencySymbol, MenuCategory, Product, ShopType } from 'models';
import { useTranslation } from 'react-i18next';
import { checkSettingCustom, getCurrencyFormat, getImageUrl } from 'utils';
import * as svg from '../../../constants/ui/svg';

interface IMenuTab {
  menuProductCategoryLoading: boolean;
  menuCategoryList: MenuCategory[];
  selectedCategory: MenuCategory;
  menuProductByCategoryList: Product[];
  handleSelectCategory: (item: MenuCategory) => void;
  cart: Cart[];
  setCart: (e: Cart[]) => void;
  onDecrease: (item: Cart) => void;
  onIncrease: (item: Cart) => void;
  isRestaurant: boolean;
  handleAddToCart: (item: Product, option: any) => void;
  setShowSelectOptionProductModal: (open: boolean) => void;
  setSelectedProduct: (product: Product) => void;
}

const MenuTab: React.FC<IMenuTab> = ({
  menuCategoryList,
  handleSelectCategory,
  selectedCategory,
  menuProductCategoryLoading,
  menuProductByCategoryList,
  cart,
  onDecrease,
  onIncrease,
  handleAddToCart,
  setShowSelectOptionProductModal,
  setSelectedProduct,
}) => {
  const { t } = useTranslation();
  const selectedShop: ShopType = useAppSelector((state) => state.shop.shopInfo);
  const settingsShopCustom = useAppSelector((state) => state.settings.settingsShopCustom);
  const billInfo: BillTypes = useAppSelector((state) => state.bill.billInfo);
  const priceFormat = (price: number) => {
    const { locale, options } = getCurrencyFormat(selectedShop.currency as CurrencySymbol);
    return price.toLocaleString(locale, options as any);
  };

  const getQuantityInCart = (item: Cart) => {
    const found = cart.find((cartItem) => cartItem.id === item.id);
    return found ? found.quantity : 0;
  };
  const isOrdered =
    checkSettingCustom({
      settingsShopCustom,
      check: 'show_order_web_order',
    }) && Boolean(billInfo.id);
  const renderCategoryButton = (item: MenuCategory) => {
    const isSelected = selectedCategory?.id === item?.id;
    return (
      <button
        key={item.id}
        onClick={() => handleSelectCategory(item)}
        className={`flex items-center border-b border-solid border-border_primary h-[60px] p-1 w-full text-ellipsis ${
          isSelected ? 'bg-[#EEEEF8] font-bold' : ''
        }`}
      >
        <div
          className={`min-w-[3px] h-full mr-1 ${!item.color ? 'bg-primary' : ''}`}
          style={item.color ? { backgroundColor: item.color } : {}}
        />
        {item.name}
      </button>
    );
  };

  const renderProductButton = (item: Product) => {
    const isItemInCart = cart.some((i) => i.id === item.id);
    const stock = typeof item.stock === 'number' ? item.stock : 0;
    const activeStock = typeof item.active_stock !== 'undefined' ? String(item.active_stock) : '1';
    const isNotSoldOut = stock > 0 || activeStock === '0';
    return (
      <button
        key={item.id}
        disabled={!billInfo?.id}
        className={`border border-solid border-border_primary flex justify-between p-3 rounded-md gap-x-2 min-h-[112px] w-full text-left bg-white disabled:bg-gray-200`}
        onClick={() => {
          if (!isOrdered || !billInfo?.id) return;
          const index = cart?.findIndex((i) => i.id === item.id);
          if (
            (`${item.active_stock}` === '1' && item.stock > 0) ||
            `${item.active_stock}` === '0'
          ) {
            const hasOptions =
              item?.product_options?.length > 0 && item?.product_options?.[0]?.options?.length > 0;
            if (hasOptions) {
              if (index > -1) {
                setSelectedProduct({ ...cart[index] } as Product);
              } else {
                setSelectedProduct({ ...item, quantity: 1 });
              }
              setShowSelectOptionProductModal(true);
            } else if (index === -1) {
              handleAddToCart(item, []);
            }
          } else {
            // toast(t('SOLD_OUT_MESSAGE', { number: item.stock }), 'warning', 2000);
          }
        }}
      >
        <div className="flex justify-between items-center font-[NotoSans] font-medium">
          <div className="flex flex-col h-full gap-2">
            <span className="text-lg text-brown_dark">{item.name}</span>
            <span className="text-lg font-bold text-primary">
              {item.price_option !== 1 ? priceFormat(item.price) : 'ASK'}
            </span>
            {isItemInCart && isNotSoldOut && (
              <div className="flex gap-x-2">
                <button
                  className="bg-primary text-sm text-white w-[40px] h-[40px] rounded-full flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onDecrease(item);
                  }}
                >
                  {svg.minusIcon}
                </button>
                <div className="w-[60px] h-[40px] rounded-lg flex items-center justify-center bg-white border border-solid border-[#E5E7EB]">
                  {getQuantityInCart(item)}
                </div>
                <button
                  className="bg-primary text-sm text-white w-[40px] h-[40px] rounded-full flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onIncrease(item);
                  }}
                >
                  {svg.plusIcon}
                </button>
              </div>
            )}
          </div>
        </div>
        {!isNotSoldOut && (
          <div className="flex flex-col gap-2 text-[#666666] font-noto-sans-regular">
            <div className="h-5"></div>
            <span>完売</span>
          </div>
        )}
        {item.image && (
          <img
            src={getImageUrl('products', 'small', item.image)}
            alt=""
            className="min-h-[88px] w-[88px] aspect-square object-cover rounded-[4px]"
          />
        )}
      </button>
    );
  };

  return (
    <>
      {/* MENU TAB */}
      <div className="left-side h-full max-w-[100px] w-full border-r border-border_primary overflow-auto">
        {[...menuCategoryList]
          .sort((a, b) => b.orderby - a.orderby)
          .filter((item) => {
            const disableApp = JSON.parse(String(item.disable_app || '[]'));
            return !disableApp.includes(9);
          })
          .map(renderCategoryButton)}
      </div>

      {!menuProductCategoryLoading && menuProductByCategoryList.length > 0 ? (
        <div className="w-full h-full p-2 space-y-2 overflow-y-auto right-side bg-gray_50">
          {[...menuProductByCategoryList]
            .sort((a, b) => {
              if (a.orderby == null && b.orderby == null) return 0;
              if (a.orderby == null) return -1;
              if (b.orderby == null) return 1;
              return a.orderby - b.orderby;
            })
            .filter((item) => {
              const disableApp = JSON.parse(String(item.disable_app || '[]'));
              return !disableApp.includes(9) && Number(item.status) === 1;
            })
            .map(renderProductButton)}
        </div>
      ) : (
        !menuProductCategoryLoading && (
          <span className="w-full py-3 text-center">{t('NO_DATA')}</span>
        )
      )}

      {menuProductCategoryLoading && (
        <div className="w-full h-full p-2 space-y-2 right-side">
          {Array.from({ length: 8 }).map((_, index) => (
            <div className="h-[112px] bg-gray-200 animate-pulse" key={index}></div>
          ))}
        </div>
      )}
    </>
  );
};

export default memo(MenuTab);
